import { zodResolver } from '@hookform/resolvers/zod';
import { TextField } from '@mui/material';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import type { DialogType } from '../../../../@types';
import type { WelcomeBonusCampaign } from '../../../../@types/api';
import { getWelcomeBonusStatusOptions } from '../../../../constants';
import { useAssignedAffiliateOptions } from '../../../../queries/affiliates';
import { createWelcomeBonusEditFormSchema, type WelcomeBonusEditFormData } from '../../../../schema';
import FormModalLayout from '../../../layouts/FormModalLayout';
import FormSelect from '../../../molecules/FormSelect';
import useWelcomeBonusCommands from './useWelcomeBonusCommands';
import { type AssignedAffiliateOptionsState, WelcomeBonusAudienceEditFields } from './WelcomeBonusAudienceFields';
import {
  WelcomeBonusFormColumn,
  WelcomeBonusFormSection,
  WelcomeBonusReviewColumns,
} from './WelcomeBonusFormPrimitives';
import { WelcomeBonusConfigurationReview, WelcomeBonusEligibilityReview } from './WelcomeBonusReview';
import {
  buildWelcomeBonusEditPayload,
  getWelcomeBonusEditFormData,
  type WelcomeBonusEditDirtyFields,
} from './welcomeBonusAdapter';

type WelcomeBonusCampaignEditFieldsProps = {
  campaign: WelcomeBonusCampaign;
  assignedAffiliates: AssignedAffiliateOptionsState;
};

const WelcomeBonusCampaignEditFields = ({ campaign, assignedAffiliates }: WelcomeBonusCampaignEditFieldsProps) => {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<WelcomeBonusEditFormData>();

  return (
    <WelcomeBonusFormSection title='Campaign'>
      <TextField
        error={!!errors.name}
        helperText={errors.name?.message}
        label='Name'
        {...register('name')}
        required
        autoFocus
      />
      <FormSelect
        label='Status'
        name='status'
        control={control}
        error={errors.status}
        options={getWelcomeBonusStatusOptions(campaign.status)}
        required
        allowDeselect={false}
      />
      <WelcomeBonusAudienceEditFields campaign={campaign} assignedAffiliates={assignedAffiliates} />
      <TextField
        error={!!errors.description}
        helperText={errors.description?.message}
        label='Description'
        {...register('description')}
        multiline
      />
    </WelcomeBonusFormSection>
  );
};

type EditWelcomeBonusFormProps = DialogType & {
  campaign: WelcomeBonusCampaign;
};

const EditWelcomeBonusForm = ({ campaign, closeModal }: EditWelcomeBonusFormProps) => {
  const { updateWelcomeBonus } = useWelcomeBonusCommands();
  const assignedAffiliatesQuery = useAssignedAffiliateOptions(campaign);
  const form = useForm<WelcomeBonusEditFormData>({
    defaultValues: getWelcomeBonusEditFormData(campaign),
    resolver: zodResolver(createWelcomeBonusEditFormSchema(campaign.status, campaign.welcomeBonusScope)),
  });
  const { dirtyFields } = form.formState;

  const handleClose = () => {
    closeModal?.();
    form.reset(getWelcomeBonusEditFormData(campaign));
  };

  const onFormSubmit = (data: WelcomeBonusEditFormData) => {
    const payload = buildWelcomeBonusEditPayload(data, dirtyFields as WelcomeBonusEditDirtyFields);
    updateWelcomeBonus(campaign.id, payload, handleClose);
  };

  const assignedAffiliates = {
    options: assignedAffiliatesQuery.data?.items ?? [],
    isFetching: assignedAffiliatesQuery.isFetching,
    isError: assignedAffiliatesQuery.isError,
    retry: () => void assignedAffiliatesQuery.refetch(),
  };

  return (
    <FormProvider {...form}>
      <FormModalLayout
        onSave={form.handleSubmit(onFormSubmit)}
        label='Edit welcome bonus'
        onClose={handleClose}
        isEdit
        showEditTabs={false}
        disableSubmit={assignedAffiliates.isFetching || assignedAffiliates.isError}
      >
        <WelcomeBonusReviewColumns>
          <WelcomeBonusFormColumn>
            <WelcomeBonusCampaignEditFields campaign={campaign} assignedAffiliates={assignedAffiliates} />
            <WelcomeBonusEligibilityReview campaign={campaign} showScope={false} />
          </WelcomeBonusFormColumn>
          <WelcomeBonusFormColumn>
            <WelcomeBonusConfigurationReview campaign={campaign} />
          </WelcomeBonusFormColumn>
        </WelcomeBonusReviewColumns>
      </FormModalLayout>
    </FormProvider>
  );
};

export default EditWelcomeBonusForm;
